/**
 * Helpers puros del panel interno de Partners (épica RES-88).
 *
 * Todo lo que no depende de React vive acá para poder testearlo sin render: períodos
 * 'YYYY-MM', escalones del programa CM, payloads de alta/edición, errores del backend
 * y el link público del formulario de solicitud.
 */

export const PROGRAMS = ['influencer', 'cm'];
export const TIERS = ['A', 'B', 'C'];
export const PARTNER_STATUSES = ['active', 'paused', 'terminated'];
export const APPLICATION_STATUSES = ['pending', 'approved', 'rejected'];
export const CODE_RE = /^[A-Z0-9]{4,20}$/;
export const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Escalones del programa CM (RES-92): porcentaje según locales activos pagos, aplicado a
 * toda la cartera. El backend los puede sobreescribir por env; esto es solo el texto
 * informativo del formulario.
 */
export const CM_TIERS = [
  { minLocations: 1, maxLocations: 3, pct: 20 },
  { minLocations: 4, maxLocations: 10, pct: 25 },
  { minLocations: 11, maxLocations: null, pct: 30 },
];

/** Normaliza el programa: cualquier cosa que no sea 'cm' es influencer (default histórico). */
export function normalizeProgram(program) {
  return program === 'cm' ? 'cm' : 'influencer';
}

/** Tier sugerido al aprobar una solicitud: B para influencer, C para CM. */
export function defaultTierForProgram(program) {
  return normalizeProgram(program) === 'cm' ? 'C' : 'B';
}

/** Código en mayúsculas, sin espacios ni caracteres fuera de A-Z0-9. */
export function normalizeCode(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

export function isValidCode(value) {
  return CODE_RE.test(String(value || ''));
}

export function isValidPeriod(value) {
  return PERIOD_RE.test(String(value || ''));
}

/** Mes anterior a `now` como 'YYYY-MM' (default del selector de cálculo). */
export function previousPeriod(now = new Date()) {
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based: el mes actual en base 0 es el anterior en base 1
  if (month === 0) return `${year - 1}-12`;
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** 'YYYY-MM' → "septiembre de 2026" (o el período crudo si no se puede formatear). */
export function formatPeriod(period, locale = 'es-AR') {
  if (!isValidPeriod(period)) return period || '—';
  const [y, m] = period.split('-').map(Number);
  try {
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
      new Date(y, m - 1, 1)
    );
  } catch (e) {
    return period;
  }
}

/** Fecha ISO → fecha local corta; '—' si falta o es inválida. */
export function formatDate(iso, locale = 'es-AR') {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleDateString(locale);
  } catch (e) {
    return d.toISOString().slice(0, 10);
  }
}

export const LOCALE_BY_LANGUAGE = { es: 'es-AR', en: 'en-US', pt: 'pt-BR' };

export function localeFor(language) {
  const base = String(language || 'es').slice(0, 2);
  return LOCALE_BY_LANGUAGE[base] || 'es-AR';
}

/**
 * Resumen del escalón CM a partir de `metrics.cmTier`.
 * @returns {{ pct: number, activeLocations: number, nextPct: number|null, missing: number|null }|null}
 */
export function cmTierSummary(cmTier) {
  if (!cmTier || typeof cmTier !== 'object') return null;
  const pct = Number(cmTier.pct) || 0;
  const next = cmTier.nextTier || null;
  let missing = null;
  if (next) {
    missing =
      cmTier.locationsToNextTier !== undefined && cmTier.locationsToNextTier !== null
        ? Number(cmTier.locationsToNextTier)
        : Math.max(0, Number(next.minLocations) - Number(cmTier.activeLocations || 0));
  }
  return {
    pct,
    activeLocations: Number(cmTier.activeLocations) || 0,
    nextPct: next ? Number(next.pct) : null,
    missing,
  };
}

/** Código de error del backend (`{ code }` en el body) o null. */
export function apiErrorCode(error) {
  return error?.response?.data?.code || null;
}

export function apiErrorStatus(error) {
  return error?.response?.status || null;
}

/** 403 del gate de plataforma: el usuario no es del equipo de Nerdo. */
export function isPlatformAdminError(error) {
  return apiErrorStatus(error) === 403 && apiErrorCode(error) === 'PLATFORM_ADMIN_REQUIRED';
}

/** Basename del router, igual que `src/App.js`. */
export function resolveBasename(pathname = '') {
  return String(pathname).startsWith('/resto/admin') ? '/resto/admin' : '';
}

/** Link público del formulario de solicitud para compartir. */
export function partnerApplyUrl(origin, pathname) {
  return `${origin || ''}${resolveBasename(pathname)}/partners/apply`;
}

export function payoutsCsvFileName(period) {
  return `payouts-${period}.csv`;
}

export const EMPTY_PARTNER_FORM = {
  code: '',
  partnerName: '',
  partnerEmail: '',
  program: 'influencer',
  tier: 'A',
  status: 'active',
  commissionPct: '',
  commissionMonths: '',
  enterpriseFlat: '',
  signOnBonus: '',
  contractStart: '',
  contractEnd: '',
};

/** Estado del formulario a partir de un partner existente (o vacío). */
export function partnerToForm(partner) {
  if (!partner) return { ...EMPTY_PARTNER_FORM };
  return {
    code: partner.code || '',
    partnerName: partner.partnerName || '',
    partnerEmail: partner.partnerEmail || '',
    program: normalizeProgram(partner.program),
    tier: partner.tier || 'A',
    status: partner.status || 'active',
    commissionPct: partner.commissionPct ?? '',
    commissionMonths: partner.commissionMonths ?? '',
    enterpriseFlat: partner.enterpriseFlat ?? '',
    signOnBonus: partner.signOnBonus ?? '',
    contractStart: (partner.contractStart || '').slice(0, 10),
    contractEnd: (partner.contractEnd || '').slice(0, 10),
  };
}

/**
 * Valida el formulario de código.
 * @returns {object} errores por campo: `{ code?, partnerName?, partnerEmail? }` con la clave
 *   del error ('invalid' | 'required'); vacío si está todo bien.
 */
export function validatePartnerForm(form, { editing = false } = {}) {
  const errors = {};
  if (!editing && !isValidCode(normalizeCode(form.code))) errors.code = 'invalid';
  if (!String(form.partnerName || '').trim()) errors.partnerName = 'required';
  if (!EMAIL_RE.test(String(form.partnerEmail || '').trim())) errors.partnerEmail = 'invalid';
  return errors;
}

/**
 * Payload para `createPartner` / `updatePartner`. Numéricos solo si el usuario cargó un
 * valor (si se omiten, el backend aplica sus defaults por tier). En CM no se mandan bono por
 * alta ni meses de comisión (no aplican; el % sale de los escalones).
 */
export function buildPartnerPayload(form, { editing = false } = {}) {
  const program = normalizeProgram(form.program);
  const payload = {
    partnerName: String(form.partnerName || '').trim(),
    partnerEmail: String(form.partnerEmail || '').trim(),
    tier: form.tier,
    program,
  };
  const numeric =
    program === 'cm'
      ? ['enterpriseFlat']
      : ['commissionPct', 'commissionMonths', 'enterpriseFlat', 'signOnBonus'];
  numeric.forEach((k) => {
    if (form[k] !== '' && form[k] !== null && form[k] !== undefined) payload[k] = Number(form[k]);
  });
  if (form.contractStart) payload.contractStart = new Date(form.contractStart).toISOString();
  if (form.contractEnd) payload.contractEnd = new Date(form.contractEnd).toISOString();
  if (editing) {
    payload.status = form.status;
  } else {
    payload.code = normalizeCode(form.code);
  }
  return payload;
}

/**
 * Payload de aprobación de una solicitud. `commissionPct` solo si se cargó y el programa es
 * influencer (en CM manda la escala por locales activos).
 */
export function buildApprovePayload(form, program) {
  const payload = {
    decision: 'approve',
    code: normalizeCode(form.code),
    tier: form.tier,
    partnerName: String(form.partnerName || '').trim(),
  };
  if (
    normalizeProgram(program) === 'influencer' &&
    form.commissionPct !== '' &&
    form.commissionPct !== null &&
    form.commissionPct !== undefined
  ) {
    payload.commissionPct = Number(form.commissionPct);
  }
  if (!payload.partnerName) delete payload.partnerName;
  return payload;
}
